// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import {
  FHAntdDateTimePicker,
  FHButton,
  FHTooltip,
  FProgressBar,
  FHSelect,
  FHConfirmDialog,
  FHAutocomplete,
  FBoxContainer,
} from '../../../base';
import { useProcedureTimeRestrictions } from '../../../../hooks';
import {
  disabledDatesOutsideRange,
  history,
  areUserRolesIn,
  disabledTimeOutsideRange,
  capitalizeFirstLetter,
} from '../../../../helpers';
import {
  btSupervisionActions,
  clientActions,
  providerAssignmentActions,
  treatmentPlanActions,
  providerActions,
} from '../../../../store';
import { getUtcOffset } from '../../../../utils/common';
import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import { ClinicalRelationships, Roles, SupervisionNoteStatus } from '../../../../constants';
import AttentionIcon from '../../../../assets/images/attention.svg';
import TrashIcon from '../../../svgs/TrashIcon3';
import ClientTimezoneDisplay from '../../ClientTimezoneDisplay';
import { BT_SUPERVISION_INITIAL_STATE, BT_SUPERVISION_PLACEHOLDERS } from './constants';
import { BT_SN_DURATION_OPTIONS, SUPERVISION_LOCATIONS, SUPERVISION_SETTINGS } from '../constants';
import { useClientCohortAssignments } from '../hooks';
import {
  getBtSupervisionClientAutocompleteOptions,
  getSelectedClientDetailsForBtSupervision,
  resolveClientIdForBtSupervisionCohortAssignments,
} from './utils/clientSelection';
import { getBayActiveAtDateTime } from '../utils/general';
import { isBay6Active, isStartBtSupervisionButtonDisabled } from './utils/general';

import type { ChangeEvent, HTMLAttributes } from 'react';
import type { Moment } from 'moment';
import type { RootState } from '../../../../store';
import type {
  IClient,
  IOption,
  IProvider,
  IProviderAssignment,
  IBtSupervision,
  SelectValueType,
} from '../../../../utils/types';

function BtSupervisionInfo() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { id: btSupervisionId } = useParams();
  const { user } = useSelector((state: any) => state.auth);
  const { list: clients = [], loading: clientsLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { loading: tpLoading, activeTreatmentPlan } = useSelector(
    (state: any) => state.treatmentPlans,
  );
  const { provider } = useSelector((state: RootState) => state.providers);

  const { btLoading, current: btSupervision } = useSelector((state: any) => state.btSupervision);
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { coAssignedProviders } = useSelector((state: any) => state.providers);

  const [btSupervisionData, setBtSupervisionData] = useState(BT_SUPERVISION_INITIAL_STATE);
  const [btSupervisionPlaceholders, setBtSupervisionPlaceholders] = useState(
    BT_SUPERVISION_PLACEHOLDERS,
  );
  const [btList, setBtList] = useState<{ id: number; label: string }[]>([]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const editable =
    areUserRolesIn(user?.roles, [Roles.Bcba]) &&
    Boolean(activeTreatmentPlan?.id) &&
    !(btSupervision?.status === SupervisionNoteStatus.Completed);
  const isClientNameSelectDisabled = Boolean(btSupervisionId) && btSupervisionId !== 'new';
  const showDeleteButton = useMemo(() => {
    return (
      btSupervision?.id &&
      btSupervisionId !== 'new' &&
      Boolean(btSupervision?.status !== SupervisionNoteStatus.Completed) &&
      (btSupervision?.bcba?.email === user?.email || user?.roles.includes(Roles.Admin))
    );
  }, [btSupervision, user]);

  const dataLoading = clientsLoading || btLoading || tpLoading || providerAssignmentLoading;

  const {
    timeToolTipOpen,
    durationToolTipOpen,
    startBetween10to12,
    endBetween10to12,
    abbreviatedZone,
    isTZIdentified,
    zone,
  } = useProcedureTimeRestrictions(
    btSupervisionData.date_start,
    Number(btSupervisionData.duration), // Convert duration to a number
    Boolean(btSupervisionId !== 'new' && !editable),
    clients.find((cl) => cl.id === btSupervisionData.client_id)?.street_address_zip ?? null,
  );

  useEffect(() => {
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [user?.roles, provider?.id]);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));

    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(btSupervisionActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!btSupervisionData.client_id) return;
    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId: btSupervisionData?.client_id },
        getAccessTokenSilently,
      }),
    );
    setBtList([]);
    if (editable && areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor])) {
      dispatch(
        providerActions.getCoAssignedProviders({
          clinical_relationship: ['prbt', 'vbt'],
          client_id: btSupervisionData?.client_id,
          getAccessTokenSilently,
        }),
      );
    } else if (editable && !areUserRolesIn(user?.roles, [Roles.Prbt])) {
      dispatch(
        providerAssignmentActions.getList({
          getAccessTokenSilently,
          clinical_relationship: ['prbt', 'vbt'],
          active_only: true,
          client_id: btSupervisionData?.client_id,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btSupervisionData?.client_id, user, editable]);

  useEffect(() => {
    if (!btSupervisionData?.client_id || tpLoading) return;
    if (!activeTreatmentPlan?.id) {
      toast.error("Client doesn't have an active treatment plan");
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTreatmentPlan, tpLoading]);

  useEffect(() => {
    if (btSupervisionId === 'new' || !btSupervisionId) {
      setBtSupervisionData(BT_SUPERVISION_INITIAL_STATE);
      setBtSupervisionPlaceholders(BT_SUPERVISION_PLACEHOLDERS);
      dispatch(btSupervisionActions.clearState());
      return;
    }
    dispatch(
      btSupervisionActions.getBtSupervision({
        btSupervisionId,
        getAccessTokenSilently,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btSupervisionId]);

  useEffect(() => {
    if (!btSupervision) return;
    const data = { ...btSupervision };
    data.date_start = moment(btSupervision.date_start);
    setBtSupervisionData(data);
    setBtSupervisionPlaceholders((btSupervisionPlaceholders) => {
      return {
        ...btSupervisionPlaceholders,
        client_id:
          btSupervision?.client?.first_name && btSupervision?.client?.last_name
            ? btSupervision?.client?.first_name + ' ' + btSupervision?.client?.last_name
            : BT_SUPERVISION_PLACEHOLDERS.client_id,
        duration:
          BT_SN_DURATION_OPTIONS.find((duration) => duration.value === btSupervision.duration)
            ?.label || BT_SUPERVISION_PLACEHOLDERS.duration,
      };
    });
  }, [btSupervision]);

  useEffect(() => {
    if (areUserRolesIn(user?.roles, [Roles.Bcba, Roles.Prbt])) {
      if (!coAssignedProviders || coAssignedProviders.length === 0) return;

      // Create BT names string
      const btData = coAssignedProviders.map((coAssignedProviderIterator: IProvider) => {
        const { id, first_name, last_name } = coAssignedProviderIterator;
        return { id, label: `${first_name} ${last_name}` };
      });
      setBtList(btData);
    } else {
      if (!providerAssignments || providerAssignments.length === 0) return;

      // Create BT names string
      const btData = providerAssignments.map((providerAssignmentIterator: IProviderAssignment) => {
        const provider = providerAssignmentIterator.provider;
        const { id, first_name, last_name } = provider;
        return { id, label: `${first_name} ${last_name}` };
      });
      setBtList(btData);
    }
  }, [providerAssignments, coAssignedProviders, user]);

  useEffect(() => {
    if (!btSupervision) return;
    setBtSupervisionPlaceholders((btSupervisionPlaceholders) => {
      return {
        ...btSupervisionPlaceholders,
        bt_provider_id:
          btList.find(
            (bt: { id: number; label: string }) => bt.id === btSupervision?.bt_provider?.id,
          )?.label || BT_SUPERVISION_PLACEHOLDERS.bt_provider_id,
      };
    });
  }, [btSupervision, btList]);

  const clientOptions = useMemo(
    () =>
      getBtSupervisionClientAutocompleteOptions({
        btSupervisionData,
        isClientNameSelectDisabled,
        userRoles: user?.roles,
        providerAssignments,
        clients,
      }),
    [btSupervisionData, isClientNameSelectDisabled, user?.roles, providerAssignments, clients],
  );

  const selectedClientDetails = useMemo(
    () => getSelectedClientDetailsForBtSupervision(btSupervisionData.client_id, clients),
    [btSupervisionData.client_id, clients],
  );

  const resolvedClientIdForCohortAssignments = useMemo(
    () =>
      resolveClientIdForBtSupervisionCohortAssignments(
        btSupervisionData.client_id,
        btSupervision?.client_id,
      ),
    [btSupervisionData.client_id, btSupervision?.client_id],
  );

  const { clientCohortAssignments, cohortAssignmentsLoading } = useClientCohortAssignments({
    clientId: resolvedClientIdForCohortAssignments,
    isEditable: editable,
    getAccessTokenSilently,
    userRoles: user?.roles,
  });

  const bayActiveAtDateTime = getBayActiveAtDateTime(
    clientCohortAssignments,
    btSupervisionData.date_start,
  );

  const pageDataLoading = dataLoading || cohortAssignmentsLoading;

  const handleFieldUpdate = async (field: string, value: string | Moment | number) => {
    if (!field) return;

    let val = value;

    if (field === 'date_start') {
      const now = moment();

      if (moment.isMoment(value) && value.isAfter(now)) {
        val = moment(); // Set value to current time, if value is in future.
      }
    }

    const data = { ...btSupervisionData, [field]: val };
    setBtSupervisionData(data);
    handlePlaceholderUpdate(field, value);
    if (Boolean(btSupervisionId) && btSupervisionId !== 'new') {
      const res = await dispatch(
        btSupervisionActions.updateBtSupervisionInfo({
          btSupervisionId,
          data,
          getAccessTokenSilently,
        }),
      );
      if (!res.payload) {
        setBtSupervisionData({ ...data, [field]: btSupervision[field] });
        return;
      }
    }
  };

  const handlePlaceholderUpdate = (field: string, value: string | Moment | number) => {
    if (!field) return;
    let renderValue;
    if (field === 'client_id') {
      const selectedClient = clients.find((client: IClient) => client.id === value);
      if (selectedClient) {
        renderValue = selectedClient.first_name + ' ' + selectedClient.last_name;
      }
    }
    if (field === 'duration') {
      renderValue = BT_SN_DURATION_OPTIONS.find((duration) => duration.value === value)?.label;
    }
    if (field === 'bt_provider_id') {
      const selectedBT = btList.find((bt: { id: number; label: string }) => bt.id === value);
      renderValue = selectedBT?.label;
    }
    if (renderValue) {
      const data = { ...btSupervisionPlaceholders, [field]: renderValue };
      setBtSupervisionPlaceholders(data);
    }
  };

  const validate = () => {
    if (!activeTreatmentPlan?.id) {
      toast.error("Client doesn't have an active treatment plan");
      return false;
    }

    const requiredFields: Array<{ field: keyof IBtSupervision; message: string }> = [
      { field: 'client_id', message: 'Please select a client' },
      { field: 'date_start', message: 'Please select a date' },
      { field: 'duration', message: 'Please select a duration' },
      { field: 'location', message: 'Please select a location' },
      { field: 'bt_provider_id', message: 'Please select a BT' },
    ];

    for (const { field, message } of requiredFields) {
      if (!btSupervisionData[field]) {
        toast.error(message);
        return false;
      }
    }

    return true;
  };

  const handleStartNotes = async () => {
    if (!validate()) return;
    const data = {
      ...btSupervisionData,
      utc_offset_date_start: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      btSupervisionActions.startBtSupervision({ data, getAccessTokenSilently }),
    );

    if (!response) {
      return;
    }

    // if response success then toast success and navigate to supervision notes page

    if (response.payload) {
      const toastId = 'btSuperVisionStarted';
      toast.success('BT Supervision started', { toastId });
      history.navigate(`/bt-supervisions/${response.payload.id}`);
    }
  };

  const handleDeleteSupervision = async () => {
    if (!btSupervision?.id || btSupervisionId === 'new') return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      btSupervisionActions.deleteBtSupervision({
        btSupervisionId: btSupervision.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  const tooltipTitle = (
    <Box data-testid="tooltipTitle" sx={{ display: 'flex', alignItems: 'center' }}>
      <img
        style={{
          width: '20px',
          height: '20px',
          marginRight: '8px',
          marginTop: '-4px',
          filter: 'brightness(84%) contrast(189%) hue-rotate(145deg) saturate(300%)',
        }}
        src={AttentionIcon}
        alt="attention-icon"
      />
      {startBetween10to12 || endBetween10to12 ? (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Supervisions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`10:00 p.m and 12:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone})` : ''
            } `}</span>
            are unlikely to be accepted by the insurance.
          </b>
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Supervisions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`12:00 a.m and 4:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone}) ` : ''
            } `}</span>
            will not be accepted by the insurance.
          </b>
        </Typography>
      )}
    </Box>
  );

  const bay6Active = isBay6Active(bayActiveAtDateTime);
  const startButtonDisabled = isStartBtSupervisionButtonDisabled({
    btSupervisionId,
    clientId: btSupervisionData.client_id,
    timeToolTipOpen,
    durationToolTipOpen,
    bayActiveAtDateTime,
  });

  return (
    <FBoxContainer contentStyle={{ pt: 2 }} sx={{ borderRadius: '4px' }}>
      <Grid container spacing={2} mb={3}>
        <Grid
          item
          xs={3.5}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Typography fontWeight="bold" sx={{ marginBottom: 1 }} textAlign="left">
            Client name
          </Typography>
          <FHAutocomplete
            data-testid="clientName"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            inputHeight={40}
            fullWidth
            placeholder="Select client"
            clearIcon={false}
            disabled={isClientNameSelectDisabled}
            getOptionLabel={(option: IOption) => option?.label ?? ''}
            isOptionEqualToValue={(option: IOption, val: IOption | null) =>
              val != null && option.id === val.id
            }
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
              return (
                <Box
                  {...props}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                  key={option.id}
                  data-testid={`clientLabel${option.id}`}
                >
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        px: 1,
                        ml: 1,
                        borderRadius: '4px',
                        color: FORTA_WHITE,
                        fontWeight: 700,
                        backgroundColor: FORTA_BLUE.light1,
                      }}
                    >
                      {capitalizeFirstLetter(option.description)}
                    </Typography>
                  )}
                </Box>
              );
            }}
            options={clientOptions ?? []}
            value={
              btSupervisionData.client_id && (clientOptions?.length ?? 0) > 0
                ? (clientOptions.find((cl: IOption) => cl.id === btSupervisionData.client_id) ??
                  null)
                : null
            }
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              const clientId = Number.isFinite(Number(newValue?.id)) ? Number(newValue?.id) : null;
              if (clientOptions.length === 1) {
                setBtSupervisionData({ ...btSupervisionData, client_id: clientId });
                handlePlaceholderUpdate('client_id', clientId ?? '');
              } else {
                handleFieldUpdate('client_id', clientId ?? '');
              }
              setBtList([]);
            }}
          />
        </Grid>
        <Grid
          item
          xs={2.8}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
        >
          <Typography fontWeight="bold" textAlign="left" sx={{ marginBottom: 1 }} noWrap>
            Start datetime
          </Typography>
          <FHTooltip
            sx={{ inset: 'auto auto -6px 0px' }}
            title={tooltipTitle}
            arrow
            open={timeToolTipOpen || startBetween10to12}
            placement="top"
            maxwidth={400}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <span style={{ width: '100%' }}>
              <FHAntdDateTimePicker
                data-testid="startDate"
                format="MM/DD/YYYY h:mm a"
                sx={{ boxShadow: 'none', width: '100%' }}
                showTime={{ format: 'h:mm a' }}
                disabled={!editable}
                allowClear={false}
                inputHeight={40}
                use12Hours
                disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -3, 0)}
                disabledTime={(current: Moment) => disabledTimeOutsideRange(current, 72)}
                value={btSupervisionData.date_start ? moment(btSupervisionData.date_start) : null}
                showNow={true}
                onChange={(value: Moment) =>
                  handleFieldUpdate('date_start', value?.seconds(0).milliseconds(0))
                }
                onSelect={(value: Moment) =>
                  handleFieldUpdate('date_start', value?.seconds(0).milliseconds(0))
                }
                error={timeToolTipOpen || startBetween10to12}
              />
            </span>
          </FHTooltip>
        </Grid>
        <Grid item xs={2.8} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography fontWeight="bold" textAlign="left" sx={{ marginBottom: 1 }} noWrap>
              Client start datetime
            </Typography>
            <FHTooltip
              sx={{ inset: 'auto auto -6px 0px' }}
              title={
                btSupervisionData.client_id
                  ? `Client's timezone is determined by their zip code ${
                      selectedClientDetails
                        ? `(Client zip code: ${selectedClientDetails.street_address_zip})`
                        : ''
                    }`
                  : 'Select a client to determine their timezone'
              }
              arrow
              placement="top"
              maxwidth={340}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_WHITE}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <Box sx={{ ml: 0.75 }}>
                <InfoIcon
                  sx={{
                    color: FORTA_BLUE.primary,
                    width: 21,
                    height: 21,
                  }}
                />
              </Box>
            </FHTooltip>
          </Box>
          <ClientTimezoneDisplay
            dateTime={
              btSupervisionData.date_start
                ? isTZIdentified
                  ? moment(btSupervisionData.date_start)
                  : null
                : null
            }
            zone={zone}
            sx={{ backgroundColor: FORTA_WHITE }}
            message={
              btSupervisionData.client_id
                ? btSupervisionData.client_id && !isTZIdentified
                  ? 'Timezone undetermined'
                  : 'Select date & time first'
                : 'Select a client first'
            }
            abbreviatedZone={abbreviatedZone}
            selectedClientId={isTZIdentified ? btSupervisionData.client_id : null}
          />
        </Grid>
        <Grid item xs={2.8}>
          <Typography fontWeight="bold" textAlign="left" sx={{ marginBottom: 1 }} noWrap>
            Duration
          </Typography>
          <FHTooltip
            sx={{ inset: 'auto auto 10px 0px' }}
            title={tooltipTitle}
            arrow
            open={durationToolTipOpen || endBetween10to12}
            placement="top"
            maxwidth={400}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <span>
              <FHSelect
                data-testid="duration"
                inputHeight={40}
                sx={{ boxShadow: 'none' }}
                fullWidth
                displayEmpty
                disabled={!editable}
                renderValue={(_) => btSupervisionPlaceholders.duration}
                options={BT_SN_DURATION_OPTIONS}
                value={btSupervisionData.duration}
                onUpdate={(value: SelectValueType) => {
                  handleFieldUpdate('duration', value);
                }}
                error={durationToolTipOpen || endBetween10to12}
              />
            </span>
          </FHTooltip>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          item
          xs={3.5}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Typography fontWeight="bold" sx={{ mr: 1, mb: 1 }} textAlign="left">
            BT name
          </Typography>
          <FHSelect
            data-testid="selectBtName"
            inputHeight={40}
            fullWidth
            sx={{ boxShadow: 'none' }}
            displayEmpty
            disabled={!btSupervisionData.client_id || !editable}
            renderValue={(_) => {
              if (!editable)
                return btSupervisionData?.bt_provider
                  ? `${btSupervisionData?.bt_provider?.first_name} ${btSupervisionData?.bt_provider?.last_name}`
                  : '';
              return btSupervisionPlaceholders.bt_provider_id;
            }}
            options={
              editable
                ? btList.map((bt: any) => ({
                    value: bt.id,
                    label: bt.label,
                  }))
                : btSupervisionData?.bt_provider
                  ? [
                      {
                        value: btSupervisionData?.bt_provider?.id,
                        label: `${btSupervisionData?.bt_provider?.first_name} ${btSupervisionData?.bt_provider?.last_name}`,
                      },
                    ]
                  : []
            }
            value={btSupervisionData?.bt_provider?.id || ''}
            onUpdate={(value: SelectValueType) => {
              if (btList.length === 1) {
                const providerId = Number.isFinite(Number(value)) ? Number(value) : null;
                setBtSupervisionData({ ...btSupervisionData, bt_provider_id: providerId });
                handlePlaceholderUpdate('bt_provider_id', value);
              } else {
                handleFieldUpdate('bt_provider_id', value);
              }
            }}
          />
        </Grid>
        <Grid
          item
          xs={2.8}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Typography fontWeight="bold" textAlign="left" sx={{ mr: 1, mb: 1 }} noWrap>
            Location
          </Typography>
          <FHSelect
            data-testid="selectLocation"
            inputHeight={40}
            fullWidth
            sx={{ boxShadow: 'none' }}
            disabled={!btSupervisionData.client_id || !editable}
            options={SUPERVISION_LOCATIONS}
            value={btSupervisionData.location || ''}
            onUpdate={(value: SelectValueType) => {
              handleFieldUpdate('location', value);
            }}
          />
        </Grid>
        <Grid
          item
          xs={2.8}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Setting
          </Typography>
          <FHSelect
            data-testid="setting"
            sx={{ width: '100%', boxShadow: 'none' }}
            inputHeight={40}
            disabled={!btSupervisionData.client_id || !editable}
            options={SUPERVISION_SETTINGS}
            value={btSupervisionData?.setting || ''}
            onUpdate={(value: SelectValueType) => {
              handleFieldUpdate('setting', value);
            }}
          />
        </Grid>
        <Grid item xs={2.85} mt={4}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FHButton
              data-testid="startBtNotes"
              sx={{ width: '100px' }}
              disabled={startButtonDisabled}
              onClick={handleStartNotes}
            >
              Start
            </FHButton>
            {showDeleteButton && (
              <IconButton
                sx={{
                  p: 1,
                  ml: 1,
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
                data-testid="deleteBtSN"
                onClick={() => setShowConfirmDeleteDialog(true)}
              >
                <TrashIcon color={FORTA_BLUE.primary} width={24} height={24} />
              </IconButton>
            )}
          </Box>
        </Grid>
        {bay6Active && (
          <Grid item xs={12}>
            <Typography sx={{ width: 'max-content', textAlign: 'left', color: FORTA_ERROR }}>
              BT supervision notes are not required for Bay 6 clients.
            </Typography>
          </Grid>
        )}
        <FHConfirmDialog
          open={showConfirmDeleteDialog}
          title={'Delete BT supervision'}
          content={
            'Are you sure you want to delete this BT supervision? This action cannot be undone.'
          }
          submittext={'Yes, delete'}
          canceltext={'Cancel'}
          submitBtnTestId={'deleteBtn'}
          cancelBtnTestId={'cancelBtn'}
          onClose={() => setShowConfirmDeleteDialog(false)}
          onSubmit={handleDeleteSupervision}
        />
      </Grid>
      {pageDataLoading && <FProgressBar />}
    </FBoxContainer>
  );
}

export default BtSupervisionInfo;
